import type { ApprovalStatus, ChangeRequest, ChangeType } from '@shared/types';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  RENAME: '名称変更',
  MERGE: '統合',
  ABOLISH: '廃止',
  MOVE: '階層移動',
};

const STATUS_STYLES: Record<ApprovalStatus, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800' },
  REJECTED: { bg: 'bg-red-100', text: 'text-red-800' },
};

const STATUS_LABELS: Record<ApprovalStatus, string> = {
  DRAFT: '下書き',
  PENDING: '承認待ち',
  APPROVED: '承認済み',
  REJECTED: '却下',
};

// Mock data
const mockChangeRequests: (ChangeRequest & { targetName: string })[] = [
  {
    id: 'cr-1',
    targetStructureId: 'dept-1',
    targetName: 'Department of Computer Science',
    changeType: 'RENAME',
    scheduledDate: '2027-04-01',
    proposedNameFormal: 'Department of Computing',
    proposedNameShort: 'Computing',
    proposedParentId: null,
    successorId: null,
    reason: 'グローバル化に対応した名称への変更',
    requestedBy: 'admin',
    requestedAt: '2026-01-05T10:00:00Z',
    approvalStatus: 'PENDING',
    approvedBy: null,
    approvedAt: null,
    rejectionReason: null,
  },
  {
    id: 'cr-2',
    targetStructureId: 'dept-2',
    targetName: 'Department of Information Engineering',
    changeType: 'MERGE',
    scheduledDate: '2027-04-01',
    proposedNameFormal: null,
    proposedNameShort: null,
    proposedParentId: null,
    successorId: 'dept-3',
    reason: '学科統合による教育体制の強化',
    requestedBy: 'faculty_admin',
    requestedAt: '2026-01-03T14:30:00Z',
    approvalStatus: 'APPROVED',
    approvedBy: 'dean',
    approvedAt: '2026-01-04T09:00:00Z',
    rejectionReason: null,
  },
  {
    id: 'cr-3',
    targetStructureId: 'course-1',
    targetName: 'AI Research Course',
    changeType: 'ABOLISH',
    scheduledDate: '2026-04-01',
    proposedNameFormal: null,
    proposedNameShort: null,
    proposedParentId: null,
    successorId: null,
    reason: '募集停止に伴う廃止',
    requestedBy: 'dept_admin',
    requestedAt: '2025-12-20T11:00:00Z',
    approvalStatus: 'REJECTED',
    approvedBy: 'dean',
    approvedAt: '2025-12-22T15:00:00Z',
    rejectionReason: '在校生の卒業まで維持が必要',
  },
];

type FilterStatus = 'ALL' | ApprovalStatus;

export default function ChangeRequestList() {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('ALL');

  const filteredRequests =
    filterStatus === 'ALL' ? mockChangeRequests : mockChangeRequests.filter((r) => r.approvalStatus === filterStatus);

  const handleApprove = (id: string) => {
    console.log('Approving:', id);
    alert(`承認しました（モック）: ${id}`);
  };

  const handleReject = (id: string) => {
    const reason = prompt('却下理由を入力してください:');
    if (reason) {
      console.log('Rejecting:', id, reason);
      alert(`却下しました（モック）: ${id}`);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">変更予約一覧</h1>
        <Link to="/admin/master/organizations">
          <Button variant="outline">組織マスタに戻る</Button>
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as FilterStatus[]).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'ALL' ? 'すべて' : STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">対象組織</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">変更種別</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">適用予定日</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">申請者</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">ステータス</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((request) => (
              <tr key={request.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/master/organizations/${request.targetStructureId}`}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {request.targetName}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {CHANGE_TYPE_LABELS[request.changeType]}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{request.scheduledDate}</td>
                <td className="px-4 py-3 text-sm">{request.requestedBy}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      STATUS_STYLES[request.approvalStatus].bg
                    } ${STATUS_STYLES[request.approvalStatus].text}`}
                  >
                    {STATUS_LABELS[request.approvalStatus]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {request.approvalStatus === 'PENDING' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApprove(request.id)}>
                        承認
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(request.id)}>
                        却下
                      </Button>
                    </div>
                  )}
                  {request.approvalStatus === 'REJECTED' && request.rejectionReason && (
                    <span className="text-sm text-red-600 cursor-help" title={request.rejectionReason}>
                      理由を表示
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filteredRequests.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  該当する変更予約はありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
